import React, { useCallback, useMemo, useRef, useState } from 'react';
import type ApexCharts from 'apexcharts';
import type { ApexOptions } from 'apexcharts';
import { Dropdown, type MenuProps } from 'antd';
import ReactApexChart from '@/components/ReactApexChartCompat';
import { DownloadIcon } from '@/assets/icons/icons';
import type {
  ChatChartDrilldown,
  ChatChartSpec,
  ChatChartType,
} from '@/common/chat/parseChatMessageParts';
import {
  chartPointDrilldown,
  chartPointValue,
} from '@/common/chat/parseChatMessageParts';

// Manually curated order: the first six are Ant Design's own preset
// swatches (Blue/Cyan/Yellow/Orange/Deep Blue/Red) — professional, subtle,
// and consistent with colors already used elsewhere in the app's design
// system. The remaining four are the louder, more-saturated hues from the
// original CVD-validated 8-set — kept last, only reached once a chart has
// more than six categories/series. Swapping in the Ant Design six means the
// CVD-safety this set previously had as a group is no longer verified
// end-to-end; re-validate before relying on it for accessibility.
const CATEGORY_COLORS = [
  '#1677FF',
  '#13C2C2',
  '#FADB14',
  '#FA8C16',
  '#2F54EB',
  '#F5222D',
  '#008300',
  '#e87ba4',
  '#eb6834',
  '#4a3aa7',
];

// The model doesn't cap category/series count, so colors cycle past 10 —
// see the note above on CVD-safety for this set.
const colorForIndex = (index: number): string =>
  CATEGORY_COLORS[index % CATEGORY_COLORS.length];

const ON_SCREEN_HEIGHT_BAR = 320;
const ON_SCREEN_HEIGHT_PIE = 380;
// A horizontal bar's categories stack along the height axis, not the width
// axis — a fixed height that works for 5 couriers cramps 20 of them, so this
// scales with category count instead (capped so a huge breakdown scrolls
// the page rather than taking it over).
const HBAR_HEIGHT_PER_CATEGORY = 34;
const HBAR_BASE_HEIGHT = 60;
const HBAR_MAX_HEIGHT = 640;
// A donut/pie's legend sits above the ring, one line per category — at a
// fixed 380px total, ApexCharts reserves room for the legend it expects but
// the ring itself doesn't shrink to match, so past ~4 categories the ring
// overflows the container and its bottom edge gets clipped. Scaling with
// category count (same idea as the horizontal-bar fix above) keeps the ring
// fully on-screen regardless of legend length.
const PIE_HEIGHT_PER_CATEGORY = 36;
const PIE_RING_AREA = 300;
const PIE_MAX_HEIGHT = 640;
// A multi-series bar/line/stacked_bar's legend (one entry per series, not
// per category) grows the same way — up to 2 series comfortably fits the
// baseline height, each one beyond that needs more room.
const MULTI_SERIES_BASELINE = 2;
const MULTI_SERIES_HEIGHT_PER_EXTRA_SERIES = 24;
const MULTI_SERIES_MAX_HEIGHT = 500;
const DOWNLOAD_WIDTH = 720;
const DOWNLOAD_SCALE = 3;

const sanitizeFileName = (s: string): string => s.replace(/[^\w\d-_ ]/g, '_');

const escapeHtml = (s: string): string =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

function apexChartType(
  type: ChatChartType
): 'bar' | 'line' | 'area' | 'donut' | 'pie' {
  if (type === 'line') return 'line';
  if (type === 'area') return 'area';
  if (type === 'donut') return 'donut';
  if (type === 'pie') return 'pie';
  return 'bar'; // bar | horizontal_bar | stacked_bar
}

type Props = {
  spec: ChatChartSpec;
  // Invoked with the clicked point's own label (for display) and its
  // drilldown (entity + filters, for the actual query) -- only ever called
  // for a point that actually has one. Provenance lives per data point, not
  // per chart (see render_chart's docstring in chatagent/tools.py), so a
  // chart can have some clickable bars/slices and some that quietly aren't.
  onDrilldown?: (label: string, drilldown: ChatChartDrilldown) => void;
};

/** Renders the chart live (hover/tooltips work, matching the rest of the
 * app's dashboard charts) with a download button overlaid top-right that
 * exports a separate, higher-resolution PNG on demand — the on-screen chart
 * itself is never swapped out for a static image. */
const ChatMessageChart: React.FC<Props> = ({ spec, onDrilldown }) => {
  const chartInstanceRef = useRef<ApexCharts | null>(null);
  const [downloading, setDownloading] = useState(false);

  const isDonutOrPie = spec.type === 'donut' || spec.type === 'pie';
  const isHorizontal = spec.type === 'horizontal_bar';
  const isStacked = spec.type === 'stacked_bar';
  const isLine = spec.type === 'line';
  const isArea = spec.type === 'area';

  const categories = spec.categories;
  const seriesCount = spec.series.length;
  // 2+ series are colored per-series — each series keeps one consistent
  // color across every category/bucket, and gets a legend so identity is
  // never color-alone. A single series is colored per-category only for
  // pie/donut, where color is the only thing distinguishing slices (there's
  // no axis); a single-series bar/horizontal_bar/stacked_bar gets one flat
  // color instead — the axis labels already distinguish the bars, so a
  // different color per bar would be redundant, not informative.
  const colorBySeries = seriesCount > 1;
  const colors = useMemo(() => {
    if (colorBySeries) return spec.series.map((_, i) => colorForIndex(i));
    if (isDonutOrPie) return categories.map((_, i) => colorForIndex(i));
    return [colorForIndex(0)];
  }, [colorBySeries, isDonutOrPie, spec.series, categories]);
  const showLegend = isDonutOrPie || colorBySeries;

  // Coarse, chart-level affordance only (ApexCharts has no easy per-bar/
  // per-slice class hook to style just the clickable ones) -- an
  // individual click still resolves per point below, so clicking a
  // non-drillable bar in an otherwise-drillable chart is a silent no-op,
  // never a wrong drill-down.
  const hasAnyDrilldown = useMemo(
    () =>
      spec.series.some((s) =>
        s.data.some((point) => chartPointDrilldown(point) !== undefined)
      ),
    [spec.series]
  );
  const isDrillable = hasAnyDrilldown && Boolean(onDrilldown);
  const handleDataPointSelection = useCallback(
    (
      _event: unknown,
      _chartContext: unknown,
      config: { seriesIndex: number; dataPointIndex: number }
    ) => {
      if (!onDrilldown) return;
      // ApexCharts' pie/donut slices are internally drawn with a constant
      // seriesIndex of 0 and the actual slice index carried on
      // dataPointIndex instead (see its Pie module: each slice's DOM node
      // gets `{index: 0, j: <sliceIndex>}`) -- the opposite convention from
      // every other chart type's own dataPointIndex-within-a-series, but
      // dataPointIndex alone happens to be correct for both. The pie/donut
      // tooltip's `seriesIndex` usage below is a separate ApexCharts code
      // path with its own, different (and, there, correct) convention --
      // don't conflate the two.
      const categoryIndex = config.dataPointIndex;
      const seriesIndex = isDonutOrPie ? 0 : config.seriesIndex;
      const point = spec.series[seriesIndex]?.data[categoryIndex];
      const drilldown =
        point !== undefined ? chartPointDrilldown(point) : undefined;
      if (!drilldown) return;
      const label = categories[categoryIndex];
      if (label) onDrilldown(label, drilldown);
    },
    [isDonutOrPie, spec.series, categories, onDrilldown]
  );

  const chartHeight = useMemo(() => {
    if (isDonutOrPie) {
      return Math.min(
        PIE_MAX_HEIGHT,
        Math.max(
          ON_SCREEN_HEIGHT_PIE,
          categories.length * PIE_HEIGHT_PER_CATEGORY + PIE_RING_AREA
        )
      );
    }
    if (isHorizontal) {
      return Math.min(
        HBAR_MAX_HEIGHT,
        Math.max(
          ON_SCREEN_HEIGHT_BAR,
          categories.length * HBAR_HEIGHT_PER_CATEGORY + HBAR_BASE_HEIGHT
        )
      );
    }
    if (colorBySeries) {
      return Math.min(
        MULTI_SERIES_MAX_HEIGHT,
        ON_SCREEN_HEIGHT_BAR +
          Math.max(0, seriesCount - MULTI_SERIES_BASELINE) *
            MULTI_SERIES_HEIGHT_PER_EXTRA_SERIES
      );
    }
    return ON_SCREEN_HEIGHT_BAR;
  }, [
    isDonutOrPie,
    isHorizontal,
    colorBySeries,
    seriesCount,
    categories.length,
  ]);

  // ApexCharts needs plain numbers -- spec.series keeps each point's
  // drilldown alongside its value (read directly by handleDataPointSelection
  // above), so this is the one place that flattens down to what ApexCharts
  // itself actually renders.
  const numericSeries = useMemo(
    () =>
      spec.series.map((s) => ({
        name: s.name,
        data: s.data.map(chartPointValue),
      })),
    [spec.series]
  );
  const pieValues = useMemo(
    () => numericSeries[0]?.data ?? [],
    [numericSeries]
  );
  // The raw sum — shown verbatim as the donut's center total, so it must
  // read 0 for an all-zero breakdown, not the `|| 1` fallback below.
  const pieRawTotal = useMemo(
    () => pieValues.reduce((sum, v) => sum + v, 0),
    [pieValues]
  );
  // `|| 1` only protects the percentage below from a divide-by-zero; never
  // reuse this one for a displayed count.
  const pieTotal = pieRawTotal || 1;
  const percentFor = useCallback(
    (index: number): string =>
      (((pieValues[index] ?? 0) / pieTotal) * 100).toFixed(1),
    [pieValues, pieTotal]
  );

  // Every key below is either present with a real value or omitted entirely —
  // ApexCharts' internal option merge treats an explicit `undefined` value as
  // "replace the default with undefined" rather than "not provided", which
  // crashes its init step (e.g. plotOptions: undefined wipes out the
  // plotOptions.line default that ApexCharts reads unconditionally).
  const options: ApexOptions = useMemo(() => {
    const titleOption: Pick<ApexOptions, 'title'> = spec.title
      ? {
          title: {
            text: spec.title,
            style: {
              fontSize: '13px',
              fontWeight: 600,
              color: 'var(--text-primary)',
            },
          },
        }
      : {};
    const legendOption: Pick<ApexOptions, 'legend'> = {
      legend: {
        show: showLegend,
        position: 'top',
        horizontalAlign: 'left',
        fontSize: '13px',
        markers: { shape: 'square' },
        itemMargin: { horizontal: 12, vertical: 6 },
      },
    };

    const base: ApexOptions = {
      chart: {
        type: apexChartType(spec.type),
        stacked: isStacked,
        toolbar: { show: false },
        fontFamily: 'inherit',
        animations: { enabled: false },
        events: {
          mounted: (chart) => {
            chartInstanceRef.current = chart;
          },
          ...(isDrillable
            ? { dataPointSelection: handleDataPointSelection }
            : {}),
        },
      },
      colors,
      dataLabels: { enabled: false },
      tooltip: { enabled: true },
      ...titleOption,
    };

    if (isDonutOrPie) {
      return {
        ...base,
        labels: categories,
        // No on-slice labels — ApexCharts has no leader-line/callout for
        // slices too small to fit a label, so the percentage lives in the
        // legend (always readable regardless of slice size) instead.
        dataLabels: { enabled: false },
        // A donut's hole is otherwise dead space — showing the total there
        // by default (swapping to the hovered slice's own name/value on
        // hover) is standard for this chart type. Plain "pie" has no hole,
        // so this is a no-op for it either way.
        ...(spec.type === 'donut'
          ? {
              plotOptions: {
                pie: {
                  // A thin ring reads as a considered chart; a thick one
                  // reads as a solid wheel of color — the same saturated
                  // hues feel "loud" purely because there's more of each one
                  // on screen. ApexCharts' default (~65%) is the latter;
                  // this leaves less of each hue visible without touching
                  // the (CVD-validated) hues themselves.
                  donut: {
                    size: '72%',
                    labels: {
                      show: true,
                      total: {
                        show: true,
                        label: 'Total',
                        fontSize: '13px',
                        color: '#757575',
                        formatter: () => pieRawTotal.toLocaleString(),
                      },
                    },
                  },
                },
              },
            }
          : {}),
        legend: {
          ...legendOption.legend,
          formatter: (legendName, opts) =>
            `${legendName} ${percentFor(opts?.seriesIndex ?? 0)}%`,
        },
        // A visible surface-color gap between slices (never a dark/colored
        // outline) so segments read as separated rather than as one solid
        // ring with a border drawn on top.
        stroke: { width: 2, colors: ['#fff'] },
        states: { hover: { filter: { type: 'darken' } } },
        tooltip: {
          enabled: true,
          custom: ({ seriesIndex }: { seriesIndex: number }) => {
            const label = escapeHtml(categories[seriesIndex] ?? '');
            const color = colors[seriesIndex % colors.length];
            const value = pieValues[seriesIndex] ?? 0;
            return (
              // Fully opaque background — ApexCharts' own default theme is
              // only 96% opacity, which read as translucent over the chart.
              '<div style="padding:8px 12px;font-size:12px;color:#000;' +
              'display:flex;align-items:center;gap:6px;background:#ffffff;' +
              'border:1px solid rgba(0,0,0,0.08);border-radius:6px;' +
              'box-shadow:0 2px 8px rgba(0,0,0,0.16);">' +
              `<span style="width:8px;height:8px;border-radius:2px;background:${color};display:inline-block;flex-shrink:0;"></span>` +
              `<strong>${label}</strong>&nbsp;${percentFor(seriesIndex)}%&nbsp;(${value})` +
              '</div>'
            );
          },
        },
      };
    }

    if (isLine || isArea) {
      // ApexCharts indexes `colors`/`markers.colors` per-SERIES for
      // line/area (unlike bar's per-category `distributed` mode) — the
      // shared `colors` above is sized to categories.length whenever
      // colorBySeries is false, which is right for bar but wrong here, so
      // a single series gets its own one-entry array instead.
      const lineColors = colorBySeries ? colors : [colorForIndex(0)];
      return {
        ...base,
        colors: lineColors,
        xaxis: {
          categories,
          labels: { style: { fontSize: '11px', colors: '#757575' } },
        },
        yaxis: {
          labels: { style: { fontSize: '11px', colors: '#757575' } },
        },
        // Straight segments, not a smoothed curve — a curve implies
        // interpolated values between buckets that were never measured.
        stroke: { width: 2, curve: 'straight' },
        markers: { size: 4, strokeWidth: 0, colors: lineColors },
        ...(isArea ? { fill: { type: 'solid', opacity: 0.15 } } : {}),
        ...legendOption,
        grid: {
          borderColor: '#F1F1F1',
          strokeDashArray: 0,
          yaxis: { lines: { show: true } },
        },
      };
    }

    // bar | horizontal_bar | stacked_bar
    return {
      ...base,
      xaxis: {
        categories,
        labels: { style: { fontSize: '11px', colors: '#757575' } },
      },
      yaxis: {
        labels: { style: { fontSize: '11px', colors: '#757575' } },
      },
      plotOptions: {
        bar: {
          horizontal: isHorizontal,
          // Leaving visible air between bars keeps a fully-saturated fill
          // from reading as one solid colored mass — narrower than
          // ApexCharts' own default, matching the mark spec's "never fill
          // the slot" rule.
          ...(isHorizontal ? { barHeight: '45%' } : { columnWidth: '45%' }),
        },
      },
      ...legendOption,
      stroke: { width: 0 },
      grid: {
        borderColor: '#F1F1F1',
        strokeDashArray: 0,
        yaxis: { lines: { show: true } },
      },
    };
  }, [
    spec.type,
    spec.title,
    isDonutOrPie,
    isHorizontal,
    isStacked,
    isLine,
    isArea,
    colorBySeries,
    showLegend,
    categories,
    colors,
    pieValues,
    pieRawTotal,
    percentFor,
    isDrillable,
    handleDataPointSelection,
  ]);

  const series = isDonutOrPie ? pieValues : numericSeries;
  const baseFileName = sanitizeFileName(spec.title || 'chart');

  const handleDownloadImage = async () => {
    const chart = chartInstanceRef.current;
    if (!chart || downloading) return;
    setDownloading(true);
    try {
      const result = await chart.dataURI({
        scale: DOWNLOAD_SCALE,
        width: DOWNLOAD_WIDTH,
      });
      if (!('imgURI' in result)) return;
      const a = document.createElement('a');
      a.href = result.imgURI;
      a.download = `${baseFileName}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      setDownloading(false);
    }
  };

  // ApexCharts' own exportToCSV — reads straight from the chart's own
  // (categories, series) data, so the export always matches what's on
  // screen without us re-deriving that shape by hand.
  const handleDownloadCsv = () => {
    chartInstanceRef.current?.exportToCSV({ fileName: baseFileName });
  };

  const downloadMenuItems: MenuProps['items'] = [
    {
      key: 'image',
      label: 'Download image',
      onClick: () => void handleDownloadImage(),
    },
    { key: 'csv', label: 'Download CSV', onClick: handleDownloadCsv },
  ];

  return (
    <div
      className={`ai-chat-drawer__chart-card${
        isDrillable ? ' ai-chat-drawer__chart-card--drillable' : ''
      }`}
    >
      <Dropdown
        menu={{ items: downloadMenuItems }}
        trigger={['click']}
        placement='bottomRight'
        disabled={downloading}
        // AIChatDrawer's own panel sits at z-index 1700 (AIChatDrawer.css) —
        // well above antd's default popup z-index (~1050), so the menu was
        // opening but rendering visually underneath the drawer. This just
        // needs to clear 1700, not match any particular antd token.
        styles={{ root: { zIndex: 1800 } }}
      >
        <button
          type='button'
          className='ai-chat-drawer__chart-download-overlay'
          disabled={downloading}
          aria-label='Download chart'
          data-tooltip='Download'
        >
          <DownloadIcon />
        </button>
      </Dropdown>
      <ReactApexChart
        options={options}
        series={series}
        type={apexChartType(spec.type)}
        width='100%'
        height={chartHeight}
      />
    </div>
  );
};

export default ChatMessageChart;
